import { Alert, Badge, Button, EmptyState, Field, Input, Skeleton } from '@personachat/ui';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { LockOpen, Plus, RotateCcw, Trash2, UsersRound } from 'lucide-react';
import { useEffect, useId, useMemo, useState, type FormEvent } from 'react';
import { Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { api } from '../../api/client';
import { lire, messageDe } from '../../api/call';
import type { components } from '../../api/schema';
import { Code, ErreurChargement, Filtre, InfoList, Jauge, Recherche, Section, Select, StatutBadge, TypeBadge } from '../../components/bits';
import { DataTable, type Colonne } from '../../components/DataTable';
import { Drawer } from '../../components/Drawer';
import { ConfirmDialog, Modal } from '../../components/Modal';
import { TabPanel, Tabs } from '../../components/Tabs';
import { useToast } from '../../components/Toast';
import { bornesJourParis, formatDate, formatDateTime, formatNombre, jourParis } from '../../lib/format';
import type { StatutCompte } from '../../lib/labels';
import { useFiltresUrl } from '../../lib/urlState';
import { PageHeader } from '../../pages/PageHeader';
import { AuditTimeline } from '../audit/AuditTimeline';

type CleDeblocage = components['schemas']['UnlockKey'];

/** L'application ne bloque pas une clé de coach à sa limite : seuls le statut et l'expiration comptent. */
export function statutCleDeblocage(k: { isActive: boolean; expiresAt?: string | null }): StatutCompte {
  if (!k.isActive) return { libelle: 'Désactivée', ton: 'neutral' };
  if (k.expiresAt && new Date(k.expiresAt).getTime() < Date.now()) return { libelle: 'Expirée', ton: 'danger' };
  return { libelle: 'Active', ton: 'success' };
}

function useClesDeblocage() {
  return useQuery({ queryKey: ['cles-deblocage'], queryFn: async () => (await lire(api.GET('/api/admin/unlock-keys'))).keys });
}

/**
 * Clés de déblocage (cahier des charges §7.5) : elles ouvrent un coach précis
 * à une élève déjà inscrite. Fiche en panneau (/cles-deblocage/:id).
 */
export function UnlockKeysPage() {
  const { valeurs: f, modifier } = useFiltresUrl({ recherche: '', statut: 'all' });
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: ouverte } = useParams();
  const cles = useClesDeblocage();
  const creer = params.get('creer') === '1';

  const filtrees = useMemo(() => {
    const q = f.recherche.trim().toLowerCase();
    return (cles.data ?? []).filter((k) => {
      if (q && ![k.key, k.description ?? '', k.personality.name].some((t) => t.toLowerCase().includes(q))) return false;
      if (f.statut !== 'all' && statutCleDeblocage(k).libelle !== f.statut) return false;
      return true;
    });
  }, [cles.data, f.recherche, f.statut]);

  const ouvrirCreation = (o: boolean) =>
    setParams(
      (a) => {
        const s = new URLSearchParams(a);
        if (o) s.set('creer', '1');
        else s.delete('creer');
        return s;
      },
      { replace: true }
    );

  const colonnes: Colonne<CleDeblocage>[] = [
    {
      id: 'code',
      header: 'Code',
      cell: (k) => (
        <div>
          <Code valeur={k.key} />
          {k.description && <div className="cell-sub">{k.description}</div>}
        </div>
      ),
    },
    {
      id: 'coach',
      header: 'Coach débloqué',
      cell: (k) => (
        <span>
          {k.personality.name}
          {!k.personality.isActive && <span className="muted small"> · désactivé</span>}
        </span>
      ),
    },
    { id: 'usage', header: 'Utilisations', cell: (k) => <Jauge utilise={k.usedCount} max={k.maxUses} indicatif /> },
    { id: 'expiration', header: 'Expiration', cell: (k) => (k.expiresAt ? formatDate(k.expiresAt) : <span className="muted">Aucune</span>) },
    {
      id: 'statut',
      header: 'Statut',
      cell: (k) => {
        const s = statutCleDeblocage(k);
        return <Badge tone={s.ton}>{s.libelle}</Badge>;
      },
    },
  ];

  return (
    <>
      <PageHeader
        title="Clés de déblocage"
        lead="Une clé ouvre un coach précis à une élève déjà inscrite. Le nombre d’utilisations sert au suivi : l’application ne bloque pas une clé au-delà."
        actions={
          <Button variant="primary" icon={<Plus size={15} aria-hidden="true" />} onClick={() => ouvrirCreation(true)}>
            Créer une clé
          </Button>
        }
      />
      <div className="toolbar" role="search">
        <Recherche id="deblocage-recherche" label="Rechercher une clé de déblocage" valeur={f.recherche} onChange={(recherche) => modifier({ recherche })} placeholder="Code, coach ou description" />
        <Filtre id="d-statut" label="Statut">
          <Select id="d-statut" value={f.statut} onChange={(e) => modifier({ statut: e.target.value })}>
            <option value="all">Tous</option>
            <option value="Active">Active</option>
            <option value="Désactivée">Désactivée</option>
            <option value="Expirée">Expirée</option>
          </Select>
        </Filtre>
      </div>
      {cles.isError ? (
        <ErreurChargement erreur={cles.error} onReessayer={() => void cles.refetch()} />
      ) : (
        <DataTable
          libelle="Clés de déblocage"
          colonnes={colonnes}
          lignes={cles.data ? filtrees : undefined}
          cleLigne={(k) => k.id}
          chargement={cles.isLoading}
          onOuvrir={(k) => navigate(`/cles-deblocage/${k.id}${location.search}`)}
          estActive={(k) => k.id === ouverte}
          vide={
            <EmptyState
              icon={<LockOpen size={28} aria-hidden="true" />}
              title={f.recherche || f.statut !== 'all' ? 'Aucune clé ne correspond' : 'Aucune clé de déblocage'}
              action={
                <Button variant="primary" onClick={() => ouvrirCreation(true)}>
                  Créer une clé
                </Button>
              }
            />
          }
        />
      )}
      {cles.data && cles.data.length > 0 && (
        <p className="note">
          {formatNombre(filtrees.length)} clé{filtrees.length > 1 ? 's' : ''} sur {formatNombre(cles.data.length)}.
        </p>
      )}
      <UnlockKeyForm open={creer} onOpenChange={ouvrirCreation} />
      <Outlet />
    </>
  );
}

function codeSuggere(): string {
  const lettres = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bloc = () => Array.from({ length: 4 }, () => lettres[Math.floor(Math.random() * lettres.length)]).join('');
  return `COACH-${bloc()}-${bloc()}`;
}

/** Création d'une clé de déblocage. */
function UnlockKeyForm({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const id = useId();
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [coach, setCoach] = useState('');
  const [description, setDescription] = useState('');
  const [maxUses, setMaxUses] = useState('20');
  const [expiration, setExpiration] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const coachs = useQuery({
    queryKey: ['coachs'],
    queryFn: async () => (await lire(api.GET('/api/admin/personalities'))).personalities,
    enabled: open,
    staleTime: 300_000,
  });

  useEffect(() => {
    if (open) {
      setCode(codeSuggere());
      setCoach('');
      setDescription('');
      setMaxUses('20');
      setExpiration('');
      setErreur(null);
    }
  }, [open]);

  const valide = code.trim().length >= 3 && coach !== '' && Number(maxUses) >= 1;

  const creer = async (e: FormEvent) => {
    e.preventDefault();
    if (!valide) return;
    setEnvoi(true);
    setErreur(null);
    try {
      const r = await lire(
        api.POST('/api/admin/unlock-keys', {
          body: {
            key: code.trim(),
            personalityId: coach,
            maxUses: Number(maxUses),
            ...(description.trim() ? { description: description.trim() } : {}),
            ...(expiration ? { expiresAt: bornesJourParis(expiration, 'fin') } : {}),
          },
        })
      );
      await queryClient.invalidateQueries({ queryKey: ['cles-deblocage'] });
      toast.succes(`Clé ${r.key.key} créée.`);
      onOpenChange(false);
      navigate(`/cles-deblocage/${r.key.id}`);
    } catch (err) {
      setErreur(messageDe(err));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Modal open={open} onOpenChange={(o) => !envoi && onOpenChange(o)} title="Créer une clé de déblocage">
      <form className="stack" onSubmit={creer}>
        <Field id={`${id}-code`} label="Code" hint="Ce que l’élève saisit dans l’application. Unique.">
          <Input id={`${id}-code`} value={code} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/\s/g, ''))} maxLength={60} />
        </Field>
        <Field id={`${id}-coach`} label="Coach débloqué">
          <Select id={`${id}-coach`} value={coach} onChange={(e) => setCoach(e.target.value)} disabled={!coachs.data}>
            <option value="">{coachs.isLoading ? 'Chargement…' : 'Choisir un coach'}</option>
            {(coachs.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.isActive ? '' : ' (désactivé)'}
              </option>
            ))}
          </Select>
        </Field>
        <Field id={`${id}-desc`} label="Description (facultatif)">
          <Input id={`${id}-desc`} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} placeholder="Ex. : Atelier logo du 12/10" />
        </Field>
        <div className="grid-2">
          <Field id={`${id}-max`} label="Utilisations prévues" hint="Pour le suivi ; non bloquant.">
            <Input id={`${id}-max`} inputMode="numeric" value={maxUses} onChange={(e) => setMaxUses(e.target.value.replace(/[^\d]/g, ''))} />
          </Field>
          <Field id={`${id}-exp`} label="Expire le (facultatif)">
            <Input id={`${id}-exp`} type="date" min={jourParis()} value={expiration} onChange={(e) => setExpiration(e.target.value)} />
          </Field>
        </div>
        {coachs.isError && <Alert>{messageDe(coachs.error)}</Alert>}
        {erreur && <Alert>{erreur}</Alert>}
        <div className="modal__footer modal__footer--inline">
          <Button onClick={() => onOpenChange(false)} disabled={envoi}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" loading={envoi} disabled={!valide}>
            Créer la clé
          </Button>
        </div>
      </form>
    </Modal>
  );
}

type OngletDeblocage = 'details' | 'eleves' | 'journal';

/** Fiche d'une clé de déblocage : modification, remise à zéro, suppression, élèves. */
export function UnlockKeyDrawer() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const onglet = (['details', 'eleves', 'journal'].includes(params.get('onglet') ?? '') ? params.get('onglet') : 'details') as OngletDeblocage;
  const cles = useClesDeblocage();
  const cle = cles.data?.find((k) => k.id === id);

  const fermer = () => {
    const s = new URLSearchParams(location.search);
    s.delete('onglet');
    navigate(`/cles-deblocage${s.toString() ? `?${s}` : ''}`);
  };

  const statut = cle ? statutCleDeblocage(cle) : null;

  return (
    <Drawer
      onClose={fermer}
      label={cle ? `Clé ${cle.key}` : 'Clé de déblocage'}
      title={cle ? <Code valeur={cle.key} /> : <Skeleton width={200} height={22} />}
      subtitle={cle ? `Ouvre ${cle.personality.name}` : undefined}
      badges={statut ? <Badge tone={statut.ton}>{statut.libelle}</Badge> : undefined}
    >
      {cles.isError ? (
        <ErreurChargement erreur={cles.error} onReessayer={() => void cles.refetch()} />
      ) : !cles.data ? (
        <Skeleton height={200} />
      ) : !cle ? (
        <EmptyState title="Clé introuvable" text="Elle a peut-être été supprimée." />
      ) : (
        <>
          <Tabs
            label="Sections de la clé"
            actif={onglet}
            onChange={(o) =>
              setParams(
                (a) => {
                  const s = new URLSearchParams(a);
                  if (o === 'details') s.delete('onglet');
                  else s.set('onglet', o);
                  return s;
                },
                { replace: true }
              )
            }
            onglets={[
              { id: 'details', label: 'Détails' },
              { id: 'eleves', label: 'Élèves' },
              { id: 'journal', label: 'Journal' },
            ]}
          />
          <TabPanel label={onglet === 'details' ? 'Détails' : onglet === 'eleves' ? 'Élèves' : 'Journal'}>
            {onglet === 'details' && <DetailsDeblocage cle={cle} onSupprimee={fermer} />}
            {onglet === 'eleves' && <ElevesDeblocage id={cle.id} />}
            {onglet === 'journal' && <AuditTimeline targetId={cle.id} />}
          </TabPanel>
        </>
      )}
    </Drawer>
  );
}

function DetailsDeblocage({ cle, onSupprimee }: { cle: CleDeblocage; onSupprimee: () => void }) {
  const id = useId();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [description, setDescription] = useState(cle.description ?? '');
  const [maxUses, setMaxUses] = useState(String(cle.maxUses));
  const [expiration, setExpiration] = useState(cle.expiresAt ? jourParis(new Date(cle.expiresAt)) : '');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [confirmer, setConfirmer] = useState<'statut' | 'zero' | 'supprimer' | null>(null);

  const expirationActuelle = cle.expiresAt ? jourParis(new Date(cle.expiresAt)) : '';
  useEffect(() => {
    setDescription(cle.description ?? '');
    setMaxUses(String(cle.maxUses));
    setExpiration(expirationActuelle);
  }, [cle.description, cle.maxUses, expirationActuelle]);

  const rafraichir = () =>
    Promise.all([queryClient.invalidateQueries({ queryKey: ['cles-deblocage'] }), queryClient.invalidateQueries({ queryKey: ['audit', cle.id] })]);

  const aChange = description.trim() !== (cle.description ?? '') || Number(maxUses) !== cle.maxUses || expiration !== expirationActuelle;

  const enregistrer = async (e: FormEvent) => {
    e.preventDefault();
    if (!aChange || Number(maxUses) < 1) return;
    setEnvoi(true);
    setErreur(null);
    try {
      await lire(
        api.PUT('/api/admin/unlock-keys/{id}', {
          params: { path: { id: cle.id } },
          body: {
            description: description.trim(),
            maxUses: Number(maxUses),
            // Chaîne vide : l'API retire l'expiration.
            expiresAt: expiration ? bornesJourParis(expiration, 'fin') : '',
          },
        })
      );
      await rafraichir();
      toast.succes('Clé modifiée.');
    } catch (err) {
      setErreur(messageDe(err));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <>
      <Section title="Utilisation">
        <InfoList
          items={[
            { label: 'Coach débloqué', value: cle.personality.name },
            { label: 'Utilisations', value: <Jauge utilise={cle.usedCount} max={cle.maxUses} indicatif /> },
            { label: 'Expiration', value: cle.expiresAt ? formatDateTime(cle.expiresAt) : 'Aucune' },
            { label: 'Création', value: formatDateTime(cle.createdAt) },
          ]}
        />
        {cle.usedCount > cle.maxUses && (
          <p className="note">Limite prévue dépassée : l’application continue d’accepter la clé. Désactivez-la pour l’arrêter.</p>
        )}
      </Section>

      <Section title="Modifier">
        <form className="stack" onSubmit={enregistrer}>
          <Field id={`${id}-desc`} label="Description">
            <Input id={`${id}-desc`} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} />
          </Field>
          <div className="grid-2">
            <Field id={`${id}-max`} label="Utilisations prévues">
              <Input id={`${id}-max`} inputMode="numeric" value={maxUses} onChange={(e) => setMaxUses(e.target.value.replace(/[^\d]/g, ''))} />
            </Field>
            <Field id={`${id}-exp`} label="Expire le" hint="Vide : sans expiration.">
              <Input id={`${id}-exp`} type="date" value={expiration} onChange={(e) => setExpiration(e.target.value)} />
            </Field>
          </div>
          {erreur && <Alert>{erreur}</Alert>}
          <div>
            <Button type="submit" variant="primary" loading={envoi} disabled={!aChange || Number(maxUses) < 1}>
              Enregistrer
            </Button>
          </div>
        </form>
      </Section>

      <Section title="Actions">
        <div className="actions-list">
          <Button onClick={() => setConfirmer('statut')}>{cle.isActive ? 'Désactiver' : 'Activer'}</Button>
          <Button icon={<RotateCcw size={15} aria-hidden="true" />} onClick={() => setConfirmer('zero')} disabled={cle.usedCount === 0}>
            Remettre le compteur à zéro
          </Button>
          <Button variant="danger" icon={<Trash2 size={15} aria-hidden="true" />} onClick={() => setConfirmer('supprimer')}>
            Supprimer
          </Button>
        </div>
      </Section>

      <ConfirmDialog
        open={confirmer === 'statut'}
        onOpenChange={(o) => !o && setConfirmer(null)}
        title={cle.isActive ? 'Désactiver la clé' : 'Activer la clé'}
        confirmLabel={cle.isActive ? 'Désactiver' : 'Activer'}
        tone={cle.isActive ? 'danger' : 'primary'}
        onConfirm={async () => {
          await lire(api.PUT('/api/admin/unlock-keys/{id}', { params: { path: { id: cle.id } }, body: { isActive: !cle.isActive } }));
          await rafraichir();
          toast.succes(cle.isActive ? 'Clé désactivée.' : 'Clé activée.');
        }}
      >
        <p>
          {cle.isActive
            ? `La clé sera refusée dans l’application. Les élèves qui ont déjà ouvert ${cle.personality.name} le gardent.`
            : 'La clé sera de nouveau acceptée dans l’application.'}
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirmer === 'zero'}
        onOpenChange={(o) => !o && setConfirmer(null)}
        title="Remettre le compteur à zéro"
        confirmLabel="Remettre à zéro"
        onConfirm={async () => {
          await lire(api.POST('/api/admin/unlock-keys/{id}/reset-usage', { params: { path: { id: cle.id } } }));
          await rafraichir();
          toast.succes('Compteur remis à zéro.');
        }}
      >
        <p>
          Le compteur passe de <strong>{formatNombre(cle.usedCount)}</strong> à 0. Les coachs déjà ouverts restent ouverts.
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirmer === 'supprimer'}
        onOpenChange={(o) => !o && setConfirmer(null)}
        title="Supprimer la clé"
        confirmLabel="Supprimer"
        tone="danger"
        retaper={cle.key}
        onConfirm={async () => {
          await lire(api.DELETE('/api/admin/unlock-keys/{id}', { params: { path: { id: cle.id } } }));
          await queryClient.invalidateQueries({ queryKey: ['cles-deblocage'] });
          toast.succes('Clé supprimée.');
          onSupprimee();
        }}
      >
        <p>La clé ne sera plus acceptée. Les élèves qui ont déjà ouvert {cle.personality.name} le gardent.</p>
      </ConfirmDialog>
    </>
  );
}

function ElevesDeblocage({ id }: { id: string }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const eleves = useQuery({
    queryKey: ['cle-deblocage', id, 'eleves', page],
    queryFn: () => lire(api.GET('/api/admin/unlock-keys/{id}/users', { params: { path: { id }, query: { page, limit: 20 } } })),
    placeholderData: keepPreviousData,
  });
  type Ligne = NonNullable<typeof eleves.data>['users'][number];
  const colonnes: Colonne<Ligne>[] = [
    {
      id: 'eleve',
      header: 'Élève',
      cell: (e) => (
        <div>
          <div className="cell-main">{e.fullName ?? e.email}</div>
          <div className="cell-sub">{e.email}</div>
        </div>
      ),
    },
    { id: 'type', header: 'Type', cell: (e) => <TypeBadge type={e.userType} isAdmin={e.isAdmin} /> },
    { id: 'statut', header: 'Statut', cell: (e) => <StatutBadge isActive={e.isActive} deletedAt={e.deletedAt} /> },
    { id: 'date', header: 'Coach ouvert le', cell: (e) => formatDateTime(e.unlockedAt) },
  ];
  if (eleves.isError) return <ErreurChargement erreur={eleves.error} onReessayer={() => void eleves.refetch()} />;
  return (
    <>
      {eleves.data && eleves.data.untracked > 0 && (
        <Alert tone="info">
          {formatNombre(eleves.data.untracked)} utilisation{eleves.data.untracked > 1 ? 's' : ''} antérieure{eleves.data.untracked > 1 ? 's' : ''} au
          suivi (lot 1 du back-office) : ces élèves ne sont pas connues.
        </Alert>
      )}
      <div style={{ marginTop: 12 }}>
        <DataTable
          libelle="Élèves qui ont utilisé la clé"
          colonnes={colonnes}
          lignes={eleves.data?.users}
          cleLigne={(e) => e.id}
          chargement={eleves.isFetching && !eleves.data}
          onOuvrir={(e) => navigate(`/eleves/${e.id}?onglet=coachs`)}
          pagination={eleves.data ? { page: eleves.data.pagination.page, pages: eleves.data.pagination.pages, total: eleves.data.pagination.total } : undefined}
          onPage={setPage}
          vide={<EmptyState icon={<UsersRound size={24} aria-hidden="true" />} title="Aucune élève suivie pour cette clé" />}
        />
      </div>
    </>
  );
}
